import Pickr from "@simonwep/pickr";
import "@simonwep/pickr/dist/themes/nano.min.css";
import interact from "interactjs";
import { CloseIcon, EditIcon } from "../../icons/icons";

// ---------------------------------------------------------------------
// Fallback icon set (used if the live metadata fetch fails/is blocked)
// ---------------------------------------------------------------------

const FALLBACK_ICONS = [
  "search",
  "close",
  "check",
  "add",
  "remove",
  "edit",
  "delete",
  "save",
  "settings",
  "home",
  "menu",
  "more_vert",
  "more_horiz",
  "arrow_back",
  "arrow_forward",
  "arrow_upward",
  "arrow_downward",
  "expand_more",
  "expand_less",
  "chevron_left",
  "chevron_right",
  "star",
  "star_border",
  "favorite",
  "favorite_border",
  "info",
  "warning",
  "error",
  "help",
  "check_circle",
  "cancel",
  "visibility",
  "visibility_off",
  "lock",
  "lock_open",
  "person",
  "group",
  "account_circle",
  "notifications",
  "notifications_none",
  "email",
  "mail",
  "send",
  "share",
  "link",
  "attach_file",
  "download",
  "upload",
  "print",
  "refresh",
  "sync",
  "filter_list",
  "sort",
  "list",
  "grid_view",
  "view_module",
  "dashboard",
  "folder",
  "folder_open",
  "description",
  "article",
  "image",
  "photo_camera",
  "videocam",
  "mic",
  "volume_up",
  "volume_off",
  "play_arrow",
  "pause",
  "stop",
  "skip_next",
  "skip_previous",
  "calendar_today",
  "event",
  "schedule",
  "access_time",
  "location_on",
  "map",
  "directions",
  "flag",
  "bookmark",
  "bookmark_border",
  "shopping_cart",
  "store",
  "payment",
  "credit_card",
  "local_offer",
  "trending_up",
  "trending_down",
  "bar_chart",
  "pie_chart",
  "insights",
  "table_chart",
  "code",
  "terminal",
  "bug_report",
  "build",
  "construction",
  "extension",
  "apps",
  "widgets",
  "palette",
  "brush",
  "format_paint",
  "text_fields",
  "title",
  "subject",
  "format_bold",
  "format_italic",
  "link_off",
  "chat",
  "chat_bubble",
  "forum",
  "question_answer",
  "thumb_up",
  "thumb_down",
  "add_circle",
  "remove_circle",
  "block",
  "done",
  "done_all",
  "pending",
  "hourglass_empty",
  "cloud",
  "cloud_upload",
  "cloud_download",
  "wifi",
  "bluetooth",
  "battery_full",
  "power_settings_new",
  "logout",
  "login",
  "key",
  "vpn_key",
  "security",
  "shield",
  "verified",
  "school",
  "work",
  "business",
  "apartment",
  "restaurant",
  "local_cafe",
  "flight",
  "directions_car",
  "directions_walk",
  "directions_bike",
  "train",
  "local_shipping",
  "public",
  "language",
  "translate",
  "accessibility",
  "psychology",
  "lightbulb",
  "science",
  "biotech",
  "engineering",
  "architecture",
  "handyman",
  "plumbing",
];

let cachedIconNames = null;
let cachedIconNamesPromise = null;

async function fetchMaterialSymbolsList() {
  if (cachedIconNames) return cachedIconNames;
  if (cachedIconNamesPromise) return cachedIconNamesPromise;

  cachedIconNamesPromise = (async () => {
    try {
      const res = await fetch(
        "https://raw.githubusercontent.com/google/material-design-icons/master/variablefont/MaterialSymbolsOutlined%5BFILL,GRAD,opsz,wght%5D.codepoints",
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const text = await res.text();

      const names = text
        .split("\n")
        .map((line) => line.trim().split(" ")[0])
        .filter(Boolean);

      cachedIconNames = names.length
        ? Array.from(new Set(names)).sort()
        : FALLBACK_ICONS;
    } catch (err) {
      console.warn("IconTool: falling back to bundled icon list —", err);
      cachedIconNames = FALLBACK_ICONS;
    }

    return cachedIconNames;
  })();

  return cachedIconNamesPromise;
}

// ---------------------------------------------------------------------
// The Tool
// ---------------------------------------------------------------------

export default class IconTool {
  static get toolbox() {
    return {
      title: "Material Icon",
      icon: '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2l2.9 6.26L21 9.27l-4.5 4.39L17.8 20 12 16.9 6.2 20l1.3-6.34L3 9.27l6.1-1.01L12 2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>',
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, api, readOnly }) {
    this.api = api;
    this.readOnly = readOnly;
    this.config = config || {};

    /**
     * CSS classes
     */
    this._CSS = {
      baseClass: this.api?.styles?.block,

      /**
       * Tool's classes
       */
      container: "icon-tool",
      icon: "icon-tool__icon",
      editButton: "icon-tool__edit-btn",

      overlay: "icon-tool-overlay",
      modal: "icon-tool-modal",

      modalHeader: "icon-tool-modal__header",
      dragHandle: "icon-tool-modal__header-drag-handle",
      search: "icon-tool-modal__search",
      close: "icon-tool-modal__close",

      grid: "icon-tool-modal__grid",
      gridItem: "icon-tool-modal__grid-item",
      selected: "is-selected",
      empty: "icon-tool-modal__empty",

      controls: "icon-tool-modal__controls",
      row: "icon-tool-modal__row",

      footer: "icon-tool-modal__footer",
      done: "icon-tool-modal__done",

      colorSwatch: "icon-tool-color-swatch",

      /**
       * Slider interaction states
       */
      sliderActive: "is-slider-active",
      sliderRowActive: "is-active",

      blockBtn: "block-btn",
      blockBtnSmall: "small",
      blockBtnLarge: "large",
      largeIcon: "large-icon",
      xlIcon: "xl-icon",
      xxlIcon: "xxl-icon",
      clear: "clear",
      save: "save",
      focusVisibleBtn: "focus-visible-btn",
      editorBlockModalBackdrop: "editor-block-modal-backdrop",
      editorBlockModal: "editor-block-modal",
      editorBlockModalTitle: "editor-block-modal-title",
      editorBlockModalLabel: "editor-block-modal-label",
      editorBlockModalText: "editor-block-modal-text",
      editorBlockModalInput: "editor-block-modal-input",
      adminFont: "admin-font",
    };

    this.data = {
      icon: data.icon || "star",
      style: data.style || this.config.defaultStyle || "outlined", // 'outlined' | 'rounded' | 'sharp'
      filled: !!data.filled,
      weight: data.weight ?? 400,
      grade: data.grade ?? 0,
      opticalSize: data.opticalSize ?? this.config.defaultSize ?? 72,
      size: data.size ?? this.config.defaultSize ?? 150,
      color: data.color || "currentColor",
    };

    this.wrapper = null;
    this.iconEl = null;
  }

  render() {
    this.wrapper = document.createElement("div");
    this.wrapper.className = this._CSS.container;

    this.iconEl = document.createElement("span");
    this._applyIconStyles();

    this.wrapper.appendChild(this.iconEl);

    if (!this.readOnly) {
      const editBtn = document.createElement("button");

      editBtn.type = "button";

      editBtn.classList.add(
        this._CSS.editButton,
        this._CSS.blockBtn,
        this._CSS.xxlIcon,
        this._CSS.blockBtnSmall,
        this._CSS.focusVisibleBtn,
      );
      editBtn.innerHTML = EditIcon;
      editBtn.title = "Change icon";

      editBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this._openPicker();
      });

      this.wrapper.appendChild(editBtn);
    }

    // hidden input hack
    // prevents empty blocks being created
    let input = document.createElement("input");
    input.setAttribute(
      "style",
      "visibility: hidden; width: 0; height: 0; padding: 0; margin: 0; border: 0; position: absolute",
    );

    this.wrapper.appendChild(input);
    return this.wrapper;
  }

  save() {
    return { ...this.data };
  }

  validate(savedData) {
    return !!savedData.icon;
  }

  // -------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------

  _applyIconStyles() {
    const { icon, style, filled, weight, grade, opticalSize, size, color } =
      this.data;

    this.iconEl.className = `${this._CSS.icon} material-symbols-${style}`;

    this.iconEl.textContent = icon;
    this.iconEl.style.fontSize = `${size}px`;
    this.iconEl.style.color = color;

    this.iconEl.style.fontVariationSettings =
      `'FILL' ${filled ? 1 : 0}, ` +
      `'wght' ${weight}, ` +
      `'GRAD' ${grade}, ` +
      `'opsz' ${opticalSize}`;
  }

  async _openPicker() {
    const overlay = document.createElement("div");
    overlay.className = this._CSS.overlay;

    const modal = document.createElement("div");
    modal.classList.add(this._CSS.modal, this._CSS.editorBlockModal);

    overlay.appendChild(modal);

    // ---------------------------------------------------------------
    // Header: search + close
    // ---------------------------------------------------------------

    const header = document.createElement("div");
    header.className = this._CSS.modalHeader;

    const dragHandle = document.createElement("div");
    dragHandle.classList.add(this._CSS.dragHandle);

    dragHandle.innerHTML = `
      <svg
        xmlns="http://www.w3.org/2000/svg"
        height="24px"
        viewBox="0 -960 960 960"
        width="24px"
        fill="#currentcolor"
      >
        <path d="M160-360v-80h640v80H160Zm0-160v-80h640v80H160Z" />
      </svg>
    `;

    const search = document.createElement("input");
    search.type = "text";
    search.placeholder = "Search Material Symbols…";
    search.className = this._CSS.search;

    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.classList.add(
      this._CSS.blockBtn,
      this._CSS.blockBtnSmall,
      this._CSS.clear,
      this._CSS.largeIcon,
    );
    closeBtn.innerHTML = CloseIcon;

    header.appendChild(dragHandle);
    header.appendChild(search);
    header.appendChild(closeBtn);

    modal.appendChild(header);

    // ---------------------------------------------------------------
    // Grid
    // ---------------------------------------------------------------

    const grid = document.createElement("div");
    grid.className = this._CSS.grid;

    const loading = document.createElement("div");
    loading.className = this._CSS.empty;
    loading.textContent = "Loading icons…";

    grid.appendChild(loading);
    modal.appendChild(grid);

    // Working copy of settings, applied live as controls change.
    const draft = { ...this.data };

    let currentNames = [];

    const renderGrid = (names) => {
      currentNames = names;
      grid.innerHTML = "";

      if (!names.length) {
        const empty = document.createElement("div");

        empty.className = this._CSS.empty;
        empty.textContent = "No icons match your search.";

        grid.appendChild(empty);
        return;
      }

      const frag = document.createDocumentFragment();

      names.slice(0, 300).forEach((name) => {
        const item = document.createElement("button");

        item.type = "button";

        item.className = [
          this._CSS.gridItem,
          name === draft.icon ? this._CSS.selected : "",
        ]
          .filter(Boolean)
          .join(" ");

        item.title = name;

        const icon = document.createElement("span");

        icon.className = `material-symbols-${draft.style}`;
        icon.textContent = name;

        item.appendChild(icon);

        item.addEventListener("click", () => {
          draft.icon = name;

          grid.querySelectorAll(`.${this._CSS.selected}`).forEach((el) => {
            el.classList.remove(this._CSS.selected);
          });

          item.classList.add(this._CSS.selected);

          applyDraftToPreview();
        });

        frag.appendChild(item);
      });

      grid.appendChild(frag);
    };

    // ---------------------------------------------------------------
    // Variable-axis + size/color controls
    // ---------------------------------------------------------------

    const controls = document.createElement("div");
    controls.className = this._CSS.controls;

    const makeRow = (labelText, inputEl) => {
      const row = document.createElement("div");
      row.className = this._CSS.row;

      const label = document.createElement("label");
      label.classList.add(this._CSS.editorBlockModalLabel, this._CSS.adminFont);
      label.textContent = labelText;

      row.appendChild(label);
      row.appendChild(inputEl);

      return row;
    };

    // ---------------------------------------------------------------
    // Style
    // ---------------------------------------------------------------

    const styleInput = document.createElement("select");

    ["outlined", "rounded", "sharp"].forEach((style) => {
      const opt = document.createElement("option");

      opt.value = style;
      opt.textContent = style[0].toUpperCase() + style.slice(1);

      if (style === draft.style) {
        opt.selected = true;
      }

      styleInput.appendChild(opt);
    });

    styleInput.addEventListener("change", () => {
      draft.style = styleInput.value;

      applyDraftToPreview();
      renderGrid(currentNames);
    });

    // ---------------------------------------------------------------
    // Filled
    // ---------------------------------------------------------------

    const filledInput = document.createElement("input");

    filledInput.type = "checkbox";
    filledInput.checked = draft.filled;

    filledInput.addEventListener("change", () => {
      draft.filled = filledInput.checked;
      applyDraftToPreview();
    });

    // ---------------------------------------------------------------
    // Weight
    // ---------------------------------------------------------------

    const weightInput = document.createElement("input");

    weightInput.type = "range";
    weightInput.min = 100;
    weightInput.max = 700;
    weightInput.step = 25;
    weightInput.value = draft.weight;

    weightInput.addEventListener("input", () => {
      draft.weight = Number(weightInput.value);
      applyDraftToPreview();
    });

    // ---------------------------------------------------------------
    // Grade
    // ---------------------------------------------------------------

    const gradeInput = document.createElement("input");

    gradeInput.type = "range";
    gradeInput.min = -25;
    gradeInput.max = 200;
    gradeInput.step = 25;
    gradeInput.value = draft.grade;

    gradeInput.addEventListener("input", () => {
      draft.grade = Number(gradeInput.value);
      applyDraftToPreview();
    });

    // ---------------------------------------------------------------
    // Optical size
    // ---------------------------------------------------------------

    const opticalInput = document.createElement("input");

    opticalInput.type = "range";
    opticalInput.min = 20;
    opticalInput.max = 48;
    opticalInput.step = 1;
    opticalInput.value = draft.opticalSize;

    opticalInput.addEventListener("input", () => {
      draft.opticalSize = Number(opticalInput.value);
      applyDraftToPreview();
    });

    // ---------------------------------------------------------------
    // Size
    // ---------------------------------------------------------------

    const sizeInput = document.createElement("input");

    sizeInput.type = "number";
    sizeInput.min = 8;
    sizeInput.max = 256;
    sizeInput.step = 1;
    sizeInput.value = draft.size;

    sizeInput.addEventListener("input", () => {
      const val = Number(sizeInput.value);

      if (Number.isNaN(val)) return;

      draft.size = val;
      applyDraftToPreview();
    });

    // ---------------------------------------------------------------
    // Color
    // ---------------------------------------------------------------

    const colorSwatch = document.createElement("div");
    colorSwatch.className = this._CSS.colorSwatch;

    // Pickr.create() does `el.parentNode.replaceChild(...)`
    // internally, so `el` must already be attached to a parent.
    const colorRow = makeRow("Color", colorSwatch);

    const colorPickr = Pickr.create({
      el: colorSwatch,
      theme: "nano",
      default: /^#/.test(draft.color) ? draft.color : "#000000",

      components: {
        preview: true,
        opacity: true,
        hue: true,

        interaction: {
          hex: true,
          input: true,
          clear: false,
          save: false,
        },
      },
    });

    colorPickr.on("change", (color) => {
      draft.color = color.toHEXA().toString();
      applyDraftToPreview();
    });

    // ---------------------------------------------------------------
    // Slider focus/isolation
    //
    // Scoped entirely to this modal.
    // No document-level pointer listeners are created.
    //
    // Pointer capture means the modal continues receiving pointerup
    // even if the user drags outside the modal before releasing.
    // ---------------------------------------------------------------

    let activeSliderRow = null;
    let activeSliderPointerId = null;

    const setActiveSlider = (input, pointerId) => {
      const row = input.closest(`.${this._CSS.row}`);
      if (!row) return;

      activeSliderRow?.classList.remove(this._CSS.sliderRowActive);

      activeSliderRow = row;
      activeSliderPointerId = pointerId;

      row.classList.add(this._CSS.sliderRowActive);
      modal.classList.add(this._CSS.sliderActive);
    };

    const clearActiveSlider = (pointerId = null) => {
      if (
        pointerId !== null &&
        activeSliderPointerId !== null &&
        pointerId !== activeSliderPointerId
      ) {
        return;
      }

      activeSliderRow?.classList.remove(this._CSS.sliderRowActive);

      activeSliderRow = null;
      activeSliderPointerId = null;

      modal.classList.remove(this._CSS.sliderActive);
    };

    modal.addEventListener("pointerdown", (event) => {
      const input = event.target.closest(
        `.${this._CSS.row} input[type="range"]`,
      );

      if (!input) return;

      setActiveSlider(input, event.pointerId);
    });

    modal.addEventListener("pointerup", (event) => {
      clearActiveSlider(event.pointerId);
    });

    modal.addEventListener("pointercancel", (event) => {
      clearActiveSlider(event.pointerId);
    });

    // ---------------------------------------------------------------
    // Drag modal
    // ---------------------------------------------------------------

    interact(modal).draggable({
      inertia: false,

      allowFrom: `.${this._CSS.dragHandle}`,

      modifiers: [
        interact.modifiers.restrictRect({
          restriction: modal.closest(".slide"),
          endOnly: true,
        }),
      ],

      listeners: {
        move(event) {
          const target = event.target;

          const x = (parseFloat(target.dataset.x) || 442) + event.dx;

          const y = (parseFloat(target.dataset.y) || 0) + event.dy;

          target.style.position = "fixed";
          target.style.top = `${y}px`;
          target.style.left = `${x}px`;

          target.dataset.x = x;
          target.dataset.y = y;
        },
      },
    });

    // ---------------------------------------------------------------
    // Append controls
    // ---------------------------------------------------------------

    controls.appendChild(makeRow("Style", styleInput));

    controls.appendChild(makeRow("Filled", filledInput));

    controls.appendChild(makeRow("Weight", weightInput));

    controls.appendChild(makeRow("Grade", gradeInput));

    controls.appendChild(makeRow("Optical size", opticalInput));

    controls.appendChild(makeRow("Size (px)", sizeInput));

    controls.appendChild(colorRow);

    modal.appendChild(controls);

    // ---------------------------------------------------------------
    // Footer
    // ---------------------------------------------------------------

    const footer = document.createElement("div");
    footer.className = this._CSS.footer;

    const doneBtn = document.createElement("button");

    doneBtn.type = "button";
    doneBtn.classList.add(this._CSS.done, this._CSS.blockBtn, this._CSS.save);
    doneBtn.textContent = "Save";

    footer.appendChild(doneBtn);
    modal.appendChild(footer);

    // ---------------------------------------------------------------
    // Live preview
    // ---------------------------------------------------------------

    const applyDraftToPreview = () => {
      const prev = this.data;

      this.data = draft;
      this._applyIconStyles();

      this.data = prev;
    };

    // ---------------------------------------------------------------
    // Commit / cancel / close
    // ---------------------------------------------------------------

    const commit = () => {
      this.data = { ...draft };
      this._applyIconStyles();

      closeOverlay();
    };

    const cancel = () => {
      this._applyIconStyles();

      closeOverlay();
    };

    const closeOverlay = () => {
      clearActiveSlider();

      document.removeEventListener("keydown", onKeydown);

      colorPickr.destroyAndRemove();

      overlay.remove();
    };

    const onKeydown = (e) => {
      if (e.key === "Escape") {
        cancel();
      }

      if (e.key === "Enter" && document.activeElement === search) {
        commit();
      }
    };

    // ---------------------------------------------------------------
    // Events
    // ---------------------------------------------------------------

    closeBtn.addEventListener("click", cancel);

    doneBtn.addEventListener("click", commit);

    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) {
        cancel();
      }
    });

    document.addEventListener("keydown", onKeydown);

    search.addEventListener("input", () => {
      const q = search.value.trim().toLowerCase();
      const all = cachedIconNames || FALLBACK_ICONS;

      const filtered = q
        ? all.filter((name) => name.replace(/_/g, " ").includes(q))
        : all;

      renderGrid(filtered);
    });

    // ---------------------------------------------------------------
    // Mount
    // ---------------------------------------------------------------

    this.wrapper.closest(".inner-container").appendChild(overlay);

    search.focus();

    const names = await fetchMaterialSymbolsList();

    renderGrid(names);
  }
}
